import React, { useState, useEffect, useMemo, useRef } from "react";
import { ScrollView, View, StyleSheet, TouchableOpacity, TextInput } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { ContactInfoForm } from "@/components/tenant/forms";
import { TermsForm } from "@/components/screens/shared/TermsForm";
import { Menu } from "@/components/Menu";
import { tenantService } from "@/services/tenantService";
import { TenantType, DEFAULT_PASSWORD_REQUIREMENTS, TenantAddressType } from "@/types/db/tenants";
import useToast from "@/hooks/useToast";
import useAuth from "@/hooks/useAuth";
import * as Crypto from "expo-crypto";
import { getEntityPermissions } from "@/lib/permissions";
import { NoPermissionScreen } from "@/components/screens/shared/NoPermissionScreen";

type AddressFormData = {
  id: string;
  name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
};

const emptyAddressForm: AddressFormData = {
  id: "",
  name: "",
  street: "",
  city: "",
  state: "",
  zip: "",
  country: "US",
};

export default function TenantSettingsPage() {
  const { colors } = useTheme();
  const { tenantId, userRoles } = useAuth();
  const toast = useToast();
  const permissions = getEntityPermissions(userRoles, "tenant");
  const [activeTab, setActiveTab] = useState<"contact" | "terms" | "security" | "addresses">("contact");
  const [meta, setMeta] = useState<TenantType["meta_data"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const baseline = useRef<TenantType["meta_data"] | null>(null);

  // Address editing state
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressForm, setAddressForm] = useState<AddressFormData>(emptyAddressForm);

  const dirty = useMemo(() => {
    if (!meta || !baseline.current) return false;
    return JSON.stringify(meta) !== JSON.stringify(baseline.current);
  }, [meta]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const metaData = await tenantService.getTenantMetaData();
      setMeta(metaData);
      baseline.current = metaData;
    } catch (e: any) {
      const errorMessage = e?.message ?? "Failed to load tenant settings.";
      setError(errorMessage);
      setMeta(null);
      baseline.current = null;

      toast.showToast({
        title: "Load failed",
        message: e?.message || "Unable to load tenant settings",
        toastType: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!meta || !dirty || !tenantId) return;

    setSaving(true);
    try {
      await tenantService.updateTenantMetaData(tenantId, meta);
      baseline.current = meta;

      toast.showToast({
        title: "Settings saved",
        message: "Your tenant settings have been updated",
        toastType: "success",
      });
    } catch (e: any) {
      toast.showToast({
        title: "Save failed",
        message: e?.message || "Unable to save tenant settings",
        toastType: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (baseline.current) {
      setMeta({ ...baseline.current });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <ThemedText>Loading tenant settings...</ThemedText>
      </ScrollView>
    );
  }

  if (error || !meta) {
    return (
      <ScrollView contentContainerStyle={styles.centered}>
        <ThemedText type="defaultSemiBold">Something went wrong</ThemedText>
        <ThemedText style={{ opacity: 0.8, marginTop: 6 }}>{error || "Unknown error"}</ThemedText>
        <Button text="Retry" icon="refresh" style={{ marginTop: 12 }} onPress={load} />
      </ScrollView>
    );
  }

  const handleContactChange = (contact: any) => {
    setMeta((m) => ({
      ...m,
      info: { ...m?.info, contact },
    }));
  };

  const handleTermsChange = (terms: Record<string, any>) => {
    setMeta((m) => ({
      ...m,
      terms,
    }));
  };

  const handlePasswordPolicyChange = (updateDays: number | undefined) => {
    setMeta((m) => ({
      ...m,
      password_policy: {
        ...m?.password_policy,
        update_days: updateDays,
      },
    }));
  };

  const handlePasswordRequirementsChange = (field: string, value: number | boolean) => {
    setMeta((m) => ({
      ...m,
      password_policy: {
        ...m?.password_policy,
        requirements: {
          ...DEFAULT_PASSWORD_REQUIREMENTS,
          ...m?.password_policy?.requirements,
          [field]: value,
        },
      },
    }));
  };

  const handleMaxFailedAttemptsChange = (attempts: number | undefined) => {
    if (attempts === undefined || (attempts >= 1 && attempts <= 10)) {
      setMeta((m) => ({
        ...m,
        max_failed_sign_in_attempts: attempts,
      }));
    }
  };

  // Address handlers
  const handleAddAddress = () => {
    setEditingAddressId(null);
    setAddressForm({ ...emptyAddressForm, id: Crypto.randomUUID() });
    setShowAddressForm(true);
  };

  const handleEditAddress = (address: TenantAddressType) => {
    setEditingAddressId(address.id);
    setAddressForm({
      id: address.id,
      name: address.name,
      street: address.street || "",
      city: address.city || "",
      state: address.state,
      zip: address.zip,
      country: address.country || "US",
    });
    setShowAddressForm(true);
  };

  const handleDeleteAddress = (id: string) => {
    setMeta((m) => ({
      ...m,
      addresses: (m?.addresses || []).filter((a) => a.id !== id),
    }));
  };

  const handleSaveAddress = () => {
    if (!addressForm.name.trim() || !addressForm.state.trim() || !addressForm.zip.trim()) {
      toast.showToast({
        title: "Validation error",
        message: "Name, state, and zip are required",
        toastType: "error",
      });
      return;
    }

    const newAddress: TenantAddressType = {
      id: addressForm.id,
      name: addressForm.name.trim(),
      street: addressForm.street.trim() || undefined,
      city: addressForm.city.trim() || undefined,
      state: addressForm.state.trim().toUpperCase(),
      zip: addressForm.zip.trim(),
      country: addressForm.country.trim() || undefined,
    };

    setMeta((m) => {
      const addresses = m?.addresses || [];
      if (editingAddressId) {
        return {
          ...m,
          addresses: addresses.map((a) => (a.id === editingAddressId ? newAddress : a)),
        };
      }
      return {
        ...m,
        addresses: [...addresses, newAddress],
      };
    });

    setShowAddressForm(false);
    setEditingAddressId(null);
    setAddressForm(emptyAddressForm);
  };

  const handleCancelAddress = () => {
    setShowAddressForm(false);
    setEditingAddressId(null);
    setAddressForm(emptyAddressForm);
  };

  // Get current password requirements with defaults
  const passwordRequirements = {
    ...DEFAULT_PASSWORD_REQUIREMENTS,
    ...meta?.password_policy?.requirements,
  };

  if (!permissions.update) {
    return <NoPermissionScreen entityName="tenant settings" />;
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <View style={styles.headerContent}>
          <View style={styles.headerInfo}>
            <ThemedText type="title">Tenant Settings</ThemedText>
            <ThemedText style={{ opacity: 0.7, marginTop: 4 }}>
              Manage contact information and employment terms
            </ThemedText>
          </View>

          <View style={styles.headerActions}>
            <ThemedText style={{ opacity: dirty ? 1 : 0.5, fontSize: 14, marginRight: 8 }}>
              {dirty ? "• Unsaved changes" : "All changes saved"}
            </ThemedText>
            <Button
              text="Reset"
              icon="backup-restore"
              style={[styles.actionButton, { backgroundColor: "transparent", borderColor: colors.border }]}
              textStyle={{ color: colors.text }}
              enabled={dirty}
              onPress={handleReset}
            />
            <Button
              text={saving ? "Saving..." : "Save"}
              icon="content-save"
              style={[styles.actionButton, { backgroundColor: colors.primary }]}
              textStyle={{ color: "white" }}
              enabled={dirty && !saving}
              onPress={handleSave}
            />
          </View>
        </View>
      </View>

      {/* Tab Navigation */}
      <View style={[styles.tabNav, { borderBottomColor: colors.border }]}>
        {[
          { key: "contact", label: "Contact" },
          { key: "terms", label: "Terms" },
          { key: "security", label: "Security" },
          { key: "addresses", label: "Addresses" },
        ].map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[styles.tab, activeTab === tab.key && { borderBottomColor: colors.primary }]}
            onPress={() => setActiveTab(tab.key as any)}
          >
            <ThemedText style={[styles.tabText, activeTab === tab.key && { color: colors.primary }]}>
              {tab.label}
            </ThemedText>
          </TouchableOpacity>
        ))}
      </View>

      {/* Tab Content */}
      <View style={styles.tabContent}>
        {activeTab === "contact" && (
          <View style={styles.section}>
            <ContactInfoForm contact={meta?.info?.contact} onChange={handleContactChange} />
          </View>
        )}

        {activeTab === "terms" && (
          <TermsForm terms={meta?.terms || {}} onUpdateTerms={handleTermsChange} editing={true} loading={loading} />
        )}

        {activeTab === "security" && (
          <ScrollView style={styles.section} contentContainerStyle={styles.securityContent}>
            {/* Password Policy Card */}
            <View style={[styles.securityCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <View style={styles.cardHeader}>
                <ThemedText type="defaultSemiBold">Password Update Policy</ThemedText>
              </View>
              <ThemedText style={[styles.cardDescription, { color: colors.textSecondary }]}>
                Require users to update their password periodically. Leave empty to disable.
              </ThemedText>

              <View style={styles.fieldRow}>
                <ThemedText style={styles.fieldLabel}>Update interval</ThemedText>
                <View style={styles.fieldInputGroup}>
                  <TextInput
                    style={[
                      styles.numberInput,
                      { backgroundColor: colors.backgroundSecondary, borderColor: colors.border, color: colors.text },
                    ]}
                    keyboardType="number-pad"
                    placeholder="90"
                    placeholderTextColor={colors.placeholder}
                    value={meta?.password_policy?.update_days?.toString() ?? ""}
                    onChangeText={(text) => {
                      const num = parseInt(text, 10);
                      handlePasswordPolicyChange(isNaN(num) ? undefined : num);
                    }}
                  />
                  <ThemedText style={{ color: colors.textSecondary }}>days</ThemedText>
                </View>
              </View>

              {meta?.password_policy?.update_days !== undefined && (
                <View
                  style={[
                    styles.validationMessage,
                    {
                      backgroundColor:
                        meta.password_policy.update_days >= 30 && meta.password_policy.update_days <= 360
                          ? (colors.teal?.background ?? colors.green.background + "20")
                          : (colors.red?.background ?? colors.red.background + "20"),
                    },
                  ]}
                >
                  <ThemedText
                    style={{
                      fontSize: 13,
                      color:
                        meta.password_policy.update_days >= 30 && meta.password_policy.update_days <= 360
                          ? (colors.teal?.text ?? colors.green.text)
                          : (colors.red?.text ?? colors.red.text),
                    }}
                  >
                    {meta.password_policy.update_days >= 30 && meta.password_policy.update_days <= 360
                      ? `Users must update their password every ${meta.password_policy.update_days} days.`
                      : "Value must be between 30 and 360 days."}
                  </ThemedText>
                </View>
              )}
            </View>

            {/* Failed Sign-in Attempts Card */}
            <View style={[styles.securityCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <View style={styles.cardHeader}>
                <ThemedText type="defaultSemiBold">Account Lockout</ThemedText>
              </View>
              <ThemedText style={[styles.cardDescription, { color: colors.textSecondary }]}>
                Lock user accounts after consecutive failed sign-in attempts. Leave empty to disable.
              </ThemedText>

              <View style={styles.fieldRow}>
                <ThemedText style={styles.fieldLabel}>Max failed attempts</ThemedText>
                <View style={styles.fieldInputGroup}>
                  <TextInput
                    style={[
                      styles.numberInput,
                      { backgroundColor: colors.backgroundSecondary, borderColor: colors.border, color: colors.text },
                    ]}
                    keyboardType="number-pad"
                    placeholder="5"
                    placeholderTextColor={colors.placeholder}
                    value={meta?.max_failed_sign_in_attempts?.toString() ?? ""}
                    onChangeText={(text) => {
                      const num = parseInt(text, 10);
                      handleMaxFailedAttemptsChange(isNaN(num) ? undefined : num);
                    }}
                  />
                  <ThemedText style={{ color: colors.textSecondary }}>attempts</ThemedText>
                </View>
              </View>

              {meta?.max_failed_sign_in_attempts !== undefined && (
                <View
                  style={[
                    styles.validationMessage,
                    {
                      backgroundColor:
                        meta.max_failed_sign_in_attempts >= 1 && meta.max_failed_sign_in_attempts <= 10
                          ? (colors.teal?.background ?? colors.green.background + "20")
                          : (colors.red?.background ?? colors.red.background + "20"),
                    },
                  ]}
                >
                  <ThemedText
                    style={{
                      fontSize: 13,
                      color:
                        meta.max_failed_sign_in_attempts >= 1 && meta.max_failed_sign_in_attempts <= 10
                          ? (colors.teal?.text ?? colors.green.text)
                          : (colors.red?.text ?? colors.red.text),
                    }}
                  >
                    {meta.max_failed_sign_in_attempts >= 1 && meta.max_failed_sign_in_attempts <= 10
                      ? `Accounts will be locked after ${meta.max_failed_sign_in_attempts} failed attempts.`
                      : "Value must be between 1 and 10 attempts."}
                  </ThemedText>
                </View>
              )}
            </View>

            {/* Password Requirements Card */}
            <View style={[styles.securityCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
              <View style={styles.cardHeader}>
                <ThemedText type="defaultSemiBold">Password Requirements</ThemedText>
              </View>
              <ThemedText style={[styles.cardDescription, { color: colors.textSecondary }]}>
                Configure password complexity requirements for all users.
              </ThemedText>

              {/* Minimum Length */}
              <View style={styles.fieldRow}>
                <ThemedText style={styles.fieldLabel}>Minimum length</ThemedText>
                <View style={styles.fieldInputGroup}>
                  <TextInput
                    style={[
                      styles.numberInput,
                      { backgroundColor: colors.backgroundSecondary, borderColor: colors.border, color: colors.text },
                    ]}
                    keyboardType="number-pad"
                    placeholder="8"
                    placeholderTextColor={colors.placeholder}
                    value={passwordRequirements.min_length?.toString() ?? "8"}
                    onChangeText={(text) => {
                      const num = parseInt(text, 10);
                      if (!isNaN(num) && num >= 6 && num <= 32) {
                        handlePasswordRequirementsChange("min_length", num);
                      }
                    }}
                  />
                  <ThemedText style={{ color: colors.textSecondary }}>characters</ThemedText>
                </View>
              </View>

              {/* Require Uppercase */}
              <View style={styles.fieldRow}>
                <ThemedText style={styles.fieldLabel}>Require uppercase letter</ThemedText>
                <TouchableOpacity
                  style={[
                    styles.toggleSwitch,
                    { backgroundColor: passwordRequirements.require_uppercase ? colors.primary : colors.border },
                  ]}
                  onPress={() =>
                    handlePasswordRequirementsChange("require_uppercase", !passwordRequirements.require_uppercase)
                  }
                >
                  <View
                    style={[
                      styles.toggleThumb,
                      {
                        backgroundColor: colors.card,
                        transform: [{ translateX: passwordRequirements.require_uppercase ? 20 : 2 }],
                      },
                    ]}
                  />
                </TouchableOpacity>
              </View>

              {/* Require Lowercase */}
              <View style={styles.fieldRow}>
                <ThemedText style={styles.fieldLabel}>Require lowercase letter</ThemedText>
                <TouchableOpacity
                  style={[
                    styles.toggleSwitch,
                    { backgroundColor: passwordRequirements.require_lowercase ? colors.primary : colors.border },
                  ]}
                  onPress={() =>
                    handlePasswordRequirementsChange("require_lowercase", !passwordRequirements.require_lowercase)
                  }
                >
                  <View
                    style={[
                      styles.toggleThumb,
                      {
                        backgroundColor: colors.card,
                        transform: [{ translateX: passwordRequirements.require_lowercase ? 20 : 2 }],
                      },
                    ]}
                  />
                </TouchableOpacity>
              </View>

              {/* Require Number */}
              <View style={styles.fieldRow}>
                <ThemedText style={styles.fieldLabel}>Require number</ThemedText>
                <TouchableOpacity
                  style={[
                    styles.toggleSwitch,
                    { backgroundColor: passwordRequirements.require_number ? colors.primary : colors.border },
                  ]}
                  onPress={() =>
                    handlePasswordRequirementsChange("require_number", !passwordRequirements.require_number)
                  }
                >
                  <View
                    style={[
                      styles.toggleThumb,
                      {
                        backgroundColor: colors.card,
                        transform: [{ translateX: passwordRequirements.require_number ? 20 : 2 }],
                      },
                    ]}
                  />
                </TouchableOpacity>
              </View>

              {/* Require Special Character */}
              <View style={styles.fieldRow}>
                <ThemedText style={styles.fieldLabel}>Require special character</ThemedText>
                <TouchableOpacity
                  style={[
                    styles.toggleSwitch,
                    { backgroundColor: passwordRequirements.require_special ? colors.primary : colors.border },
                  ]}
                  onPress={() =>
                    handlePasswordRequirementsChange("require_special", !passwordRequirements.require_special)
                  }
                >
                  <View
                    style={[
                      styles.toggleThumb,
                      {
                        backgroundColor: colors.card,
                        transform: [{ translateX: passwordRequirements.require_special ? 20 : 2 }],
                      },
                    ]}
                  />
                </TouchableOpacity>
              </View>
            </View>
          </ScrollView>
        )}

        {activeTab === "addresses" && (
          <ScrollView style={styles.section} contentContainerStyle={styles.addressesContent}>
            {/* Add Address Form */}
            {showAddressForm && (
              <View style={[styles.addressCard, { backgroundColor: colors.card, borderColor: colors.border }]}>
                <View style={styles.addressForm}>
                  <TextInput
                    style={[
                      styles.addressInput,
                      { backgroundColor: colors.backgroundSecondary, borderColor: colors.border, color: colors.text },
                    ]}
                    placeholder="Name (e.g., Billing, Shipping, HQ) *"
                    placeholderTextColor={colors.placeholder}
                    value={addressForm.name}
                    onChangeText={(text) => setAddressForm((f) => ({ ...f, name: text }))}
                    autoFocus
                  />
                  <TextInput
                    style={[
                      styles.addressInput,
                      { backgroundColor: colors.backgroundSecondary, borderColor: colors.border, color: colors.text },
                    ]}
                    placeholder="Street"
                    placeholderTextColor={colors.placeholder}
                    value={addressForm.street}
                    onChangeText={(text) => setAddressForm((f) => ({ ...f, street: text }))}
                  />
                  <View style={styles.addressRow}>
                    <TextInput
                      style={[
                        styles.addressInput,
                        styles.cityInput,
                        { backgroundColor: colors.backgroundSecondary, borderColor: colors.border, color: colors.text },
                      ]}
                      placeholder="City"
                      placeholderTextColor={colors.placeholder}
                      value={addressForm.city}
                      onChangeText={(text) => setAddressForm((f) => ({ ...f, city: text }))}
                    />
                    <TextInput
                      style={[
                        styles.addressInput,
                        styles.stateInput,
                        { backgroundColor: colors.backgroundSecondary, borderColor: colors.border, color: colors.text },
                      ]}
                      placeholder="ST *"
                      placeholderTextColor={colors.placeholder}
                      value={addressForm.state}
                      onChangeText={(text) => setAddressForm((f) => ({ ...f, state: text.toUpperCase().slice(0, 2) }))}
                      maxLength={2}
                      autoCapitalize="characters"
                    />
                    <TextInput
                      style={[
                        styles.addressInput,
                        styles.zipInput,
                        { backgroundColor: colors.backgroundSecondary, borderColor: colors.border, color: colors.text },
                      ]}
                      placeholder="Zip *"
                      placeholderTextColor={colors.placeholder}
                      value={addressForm.zip}
                      onChangeText={(text) =>
                        setAddressForm((f) => ({ ...f, zip: text.replace(/\D/g, "").slice(0, 5) }))
                      }
                      keyboardType="number-pad"
                      maxLength={5}
                    />
                  </View>
                  <View style={styles.addressFormActions}>
                    <Button
                      text="Cancel"
                      style={[styles.formBtn, { borderColor: colors.border }]}
                      textStyle={{ fontSize: 14 }}
                      onPress={handleCancelAddress}
                    />
                    <Button
                      text={editingAddressId ? "Update" : "Add"}
                      icon={editingAddressId ? "check" : "plus"}
                      iconPosition="right"
                      style={[styles.formBtn, { backgroundColor: colors.primary }]}
                      textStyle={{ fontSize: 14, color: "white" }}
                      onPress={handleSaveAddress}
                      enabled={!!addressForm.name.trim() && !!addressForm.state.trim() && !!addressForm.zip.trim()}
                    />
                  </View>
                </View>
              </View>
            )}

            {/* Address List */}
            {(meta?.addresses || []).map((address) => (
              <View
                key={address.id}
                style={[styles.addressCard, { backgroundColor: colors.card, borderColor: colors.border }]}
              >
                <View style={styles.addressCardContent}>
                  <View style={styles.addressInfo}>
                    <ThemedText type="defaultSemiBold">{address.name}</ThemedText>
                    {address.street && (
                      <ThemedText style={{ color: colors.textSecondary, fontSize: 14 }}>{address.street}</ThemedText>
                    )}
                    <ThemedText style={{ color: colors.textSecondary, fontSize: 14 }}>
                      {[address.city, address.state, address.zip].filter(Boolean).join(", ")}
                    </ThemedText>
                  </View>
                  <Menu
                    buttonStyle={{ borderWidth: 0, backgroundColor: "transparent" }}
                    hideArchive
                    onEdit={() => handleEditAddress(address)}
                    onDelete={() => handleDeleteAddress(address.id)}
                  />
                </View>
              </View>
            ))}

            {/* Add Button */}
            {!showAddressForm && (
              <Button
                text="Add Address"
                icon="plus"
                iconPosition="right"
                style={[styles.addAddressBtn, { borderColor: colors.border }]}
                textStyle={{ fontSize: 14 }}
                onPress={handleAddAddress}
              />
            )}

            {/* Empty State */}
            {(meta?.addresses || []).length === 0 && !showAddressForm && (
              <View style={styles.emptyState}>
                <ThemedText style={{ color: colors.textSecondary, textAlign: "center" }}>
                  No addresses yet. Add your billing, shipping, or office addresses.
                </ThemedText>
              </View>
            )}
          </ScrollView>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: 24,
  },
  header: {
    // paddingHorizontal: 16,
    // paddingVertical: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
  },
  headerContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
  },
  headerInfo: {
    flex: 1,
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  actionButton: {
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
  tabNav: {
    flexDirection: "row",
    borderBottomWidth: 1,
  },
  tab: {
    flex: 1,
    alignItems: "center",
    paddingVertical: 12,
    borderBottomWidth: 2,
    borderBottomColor: "transparent",
  },
  tabText: {
    fontSize: 14,
    fontWeight: "500",
  },
  tabContent: {
    flex: 1,
  },
  section: {
    padding: 16,
    gap: 16,
  },
  securityContent: {
    gap: 16,
  },
  securityCard: {
    padding: 16,
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    gap: 12,
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  cardDescription: {
    fontSize: 13,
    lineHeight: 18,
  },
  fieldRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: 4,
  },
  fieldLabel: {
    fontSize: 14,
  },
  fieldInputGroup: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  numberInput: {
    width: 80,
    height: 40,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    textAlign: "center",
    fontSize: 16,
  },
  validationMessage: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  // Address styles
  addressesContent: {
    gap: 12,
  },
  addressCard: {
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: "hidden",
  },
  addressCardContent: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 16,
  },
  addressInfo: {
    flex: 1,
    gap: 2,
  },
  addressForm: {
    padding: 16,
    gap: 12,
  },
  addressInput: {
    height: 44,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 15,
  },
  addressRow: {
    flexDirection: "row",
    gap: 8,
  },
  cityInput: {
    flex: 2,
  },
  stateInput: {
    width: 60,
    textAlign: "center",
  },
  zipInput: {
    width: 80,
  },
  addressFormActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 4,
  },
  formBtn: {
    height: 36,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
    borderRadius: 8,
  },
  addAddressBtn: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderStyle: "dashed",
    borderRadius: 12,
    height: 48,
  },
  emptyState: {
    padding: 24,
    alignItems: "center",
  },
  toggleSwitch: {
    width: 44,
    height: 24,
    borderRadius: 12,
    justifyContent: "center",
    padding: 2,
  },
  toggleThumb: {
    width: 20,
    height: 20,
    borderRadius: 10,
    boxShadow: "0 2px 2.22px 0 rgba(0, 0, 0, 0.22)",
    elevation: 3,
  },
});
